import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { useTraining } from '../hooks/useTraining';
import TrainingCourseCard from '../components/TrainingCourseCard';
import { TrainingCourse } from '../services/trainingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function TrainingDashboardPage() {
  const { courses, myProgress, loading, error, fetchCourses, fetchMyProgress } = useTraining();
  const [tabValue, setTabValue] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([
        fetchCourses(),
        fetchMyProgress(),
      ]);
    } catch (err) {
      console.error('Failed to load training data:', err);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filter courses based on tab and filters
  const getFilteredCourses = () => {
    let filtered = [...courses];

    // Tab-based filtering
    if (tabValue === 1) {
      // Recommended courses
      filtered = filtered.filter(c => c.isRecommended);
    } else if (tabValue === 2) {
      // Required courses
      filtered = filtered.filter(c => c.isRequired);
    } else if (tabValue === 3) {
      // In Progress - courses with active progress
      const inProgressCourseIds = myProgress
        .filter(p => p.status === 'IN_PROGRESS')
        .map(p => p.courseId);
      filtered = filtered.filter(c => inProgressCourseIds.includes(c.id));
    } else if (tabValue === 4) {
      // Completed
      const completedCourseIds = myProgress
        .filter(p => p.status === 'COMPLETED')
        .map(p => p.courseId);
      filtered = filtered.filter(c => completedCourseIds.includes(c.id));
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(c => c.category === categoryFilter);
    }

    // Difficulty filter
    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(c => c.difficultyLevel === difficultyFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        c =>
          c.title.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  // Get unique categories
  const categories = Array.from(new Set(courses.map(c => c.category).filter(Boolean)));

  // Calculate stats
  const stats = {
    total: courses.length,
    inProgress: myProgress.filter(p => p.status === 'IN_PROGRESS').length,
    completed: myProgress.filter(p => p.status === 'COMPLETED').length,
    totalHours: Math.round(
      myProgress
        .filter(p => p.status === 'COMPLETED')
        .reduce((acc, p) => {
          const course = courses.find(c => c.id === p.courseId);
          return acc + (course?.estimatedDuration || 0);
        }, 0) / 60
    ),
  };

  const filteredCourses = getFilteredCourses();

  if (loading && courses.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          My Training
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete training courses to enhance your skills and earn certificates
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Available Courses
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                In Progress
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {stats.inProgress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Completed
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Training Hours
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.totalHours}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Courses" />
          <Tab label="Recommended" />
          <Tab label="Required" />
          <Tab label={`In Progress (${stats.inProgress})`} />
          <Tab label={`Completed (${stats.completed})`} />
        </Tabs>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search courses"
            placeholder="Search by title or description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            select
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            select
            label="Difficulty"
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All Levels</MenuItem>
            <MenuItem value="BEGINNER">Beginner</MenuItem>
            <MenuItem value="INTERMEDIATE">Intermediate</MenuItem>
            <MenuItem value="ADVANCED">Advanced</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {/* Course Grid */}
      <TabPanel value={tabValue} index={tabValue}>
        {filteredCourses.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No courses found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || categoryFilter !== 'all' || difficultyFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Check back later for new training opportunities'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredCourses.map((course) => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <TrainingCourseCard course={course} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
    </Container>
  );
}

